import * as React from 'react';
import SuperCritical from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';

import { PageAction } from './pagestateenums';
import { PastExports, ExportConfirmationPopup, ExportFinishedStatus } from './pastexports';
import { PRICE_PRECISION } from './exportgrouplist';

import { ActionLoadingIndicator, PageLoadingIndicator } from 'core/actionloadingindicator/view';
import { ActionsMenuButton } from 'portcall/actionsmenubutton/view';
import { DateRangeSelectorView } from 'portcall/daterangeselector/view';
import { ExpandCollapseLink } from 'portcall/gridcomponents/view';
import { ExportGroupListView } from './exportgrouplistview';
import { ExportSelectionControls } from './exportselectioncontrols';
import { ExportToQuickBooksLink } from './exporttoquickbookslink';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { PCModal } from 'portcall/pcmodal/view';

export const PastExportsView = SuperCritical(({ model: m } : { model : PastExports }) =>
    <div className="pastexports pc-page-fixedwidth">
        <div className="pc-actions-links-header">
            <a href="" className="pc-back-link" onClick={noDefault(() => window.history.back())}>&lt;&lt; Back</a>
            <ActionsMenuButton onClick={() => m.ShowActionsMenu(!m.ShowActionsMenu())} />
            <ActionsContainer model={m} />
        </div>
        <div className="pc-page-title">Pilot Billing</div>
        <ExportSelectionControls model={m} />
        <FilterControls model={m} />
        <PageLoadingIndicator loading={m.Loading} />
        <ExportGroupListView model={m.ExportGroupList} />
        <ExportConfirmationPopupView model={m} />
    </div>
);

let ActionsContainer = SuperCritical(({ model: m } : { model : PastExports }) =>
    <div className="pc-actions">
        {m.ShowActionsMenu() &&
            <PCDismissible className="pc-card pc-actions-menu" onDismiss={() => m.ShowActionsMenu(false)}>
                <div className="action-item">
                    <div>
                        <ExpandCollapseLink model={m.RowExpander} actionWrapper={m.MenuAction} />
                    </div>
                    <div className="pc-text-smalllabel pc-action-item-descr">Collapse all of the items for a compressed view.</div>
                </div>
                <div className="action-item">
                    <div>
                        <ExportToQuickBooksLink model={m} />
                    </div>
                    <div className="pc-text-smalllabel pc-action-item-descr">Export the following items to QuickBooks.</div>
                </div>
            </PCDismissible>
        }
    </div>
);

let FilterControls = SuperCritical(({ model: m } : { model : PastExports }) =>
    <div className="filter-controls">
        <span className="subtitle">Past Exports</span>
        <a className="exports-nav-toggle-link"
            href={`#!pendingexports?id=${m.Model().Id()}&expansionMode=${m.RowExpander.ExpansionMode()}&filter=${m.Filter()}`}>
            Show Pending Exports
        </a>
        <input className="pc-reports-filter" placeholder="Filter by &mdash;"
            value={m.Filter()}
            onChange={e => m.Filter(e.currentTarget.value)} />
        <DateRangeSelectorView model={m.DateRangeSelector} />
    </div>
);

let ExportConfirmationPopupView = SuperCritical(({ model: m } : { model : PastExports }) => {
    const popup = m.ExportConfirmationPopup();

    return (
        <PCModal className="export-confirmation-popup" shown={() => !!popup}>
            <div className="export-confirmation-popup-content">
                <div className="export-confirmation-header">QuickBooks Export</div>
                {popup && (popup.ExportFinishedStatus() ?
                    <ExportConfirmationPostexportView model={m} popup={popup} finishedStatus={popup.ExportFinishedStatus()!} /> :
                    <ExportConfirmationPreexportView model={m} popup={popup} />
                )}
            </div>
        </PCModal>
    );
});

let ExportConfirmationPreexportView = SuperCritical(({ popup, model: m } : { popup : ExportConfirmationPopup, model : PastExports }) =>
    <div>
        <div className="export-confirmation-body">
            <div className="pc-text-smalllabel">
                Export invoice data to a QuickBooks IIF file.
            </div>
            <ActionLoadingIndicator loading={popup.SendingExportRequest} />
            <div className="export-setting pc-text-billing-label">
                <span className="export-setting-label">
                    Number of invoices:
                </span>
                <span className="export-setting-value">
                    {popup.NumberOfInvoices}
                </span>
            </div>
            <div className="export-setting pc-text-billing-label">
                <span className="export-setting-label">
                    Total value of all invoices:
                </span>
                <span className="export-setting-value">
                    {popup.ExportTotal.toString({ places: PRICE_PRECISION })}
                </span>
            </div>
        </div>
        <div className="export-confirmation-footer">
            <a href="" onClick={noDefault(popup.Cancel)}>No, don't export</a>
            <button className="pc-button" onClick={popup.SendExportRequest}>Export</button>
        </div>
    </div>
);

let ExportConfirmationPostexportView = SuperCritical(({ popup, finishedStatus, model: m } : { popup : ExportConfirmationPopup, finishedStatus : ExportFinishedStatus, model : PastExports }) =>
    <div>
        {finishedStatus.success ?
            <ExportConfirmationSuccessBody /> :
            <ExportConfirmationUnexpectedErrorBody errorDetails={finishedStatus.errorDetails} popup={popup} />
        }
        <div className="export-confirmation-footer">
            <button className="pc-button" onClick={popup.Cancel}>OK</button>
        </div>
    </div>
);

let ExportConfirmationSuccessBody = SuperCritical(() =>
    <div className="export-confirmation-body">
        Your invoices have been exported successfully.
        A download should have begun in your browser.
        Please import the downloaded IIF file into QuickBooks to finish the export.
        If the download does not begin in a few seconds,
        please try re-exporting your invoices.
    </div>
);

let ExportConfirmationUnexpectedErrorBody = SuperCritical(({ errorDetails, popup } : { errorDetails : string, popup : ExportConfirmationPopup }) =>
    <div className="export-confirmation-body">
        <div className="generic-message">
            We encountered an unexpected error formatting your QuickBooks IIF file for download.
            Please try re-exporting your invoices.
            If this problem persists, please contact
            {' '}
            <a href={popup.SupportHref()}>Support</a>.
            We apologize for the inconvenience.
        </div>
        <div className="error-details">
            Error details:
            <br />
            <textarea value={errorDetails} disabled rows={6} />
        </div>
    </div>
);


// WEBPACK FOOTER //
// ./apps/portcall/quickbooksexports/pastexportsview.tsx